import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Categories accepted by POST /insights/requirements/add/. Each label must match
// a key in the backend's _VALID_CATEGORIES map. `hint` powers the (?) tooltip.
const CATEGORIES = [
  {
    label: "Technical Requirements",
    hint: "Solution capabilities and technical approaches requested by the RFP, including architecture, security, integrations, and performance.",
  },
  {
    label: "Compliance & Legal",
    hint: "Standards, certifications, and regulatory requirements in the RFP, including compliance frameworks, privacy laws, and governance obligations.",
  },
  {
    label: "Deliverables & Timeline",
    hint: "Expected project deliverables and timing requirements, including milestones, implementation plans, training, and ongoing support.",
  },
  {
    label: "Evaluation Criteria",
    hint: "Requirements used to evaluate and compare proposals, including qualifications, experience, references, and scoring criteria.",
  },
  {
    label: "Custom Requirement",
    hint: "",
  },
];

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Chevron = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const HelpIcon = ({ text }) => {
  const ref = useRef(null);
  const [coords, setCoords] = useState(null);

  const show = () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setCoords({ left: r.right + 10, top: r.top + r.height / 2 });
  };

  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={() => setCoords(null)}
      onClick={(e) => e.stopPropagation()}
      className="text-[#9CA3AF] cursor-help flex-shrink-0 inline-flex"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      {coords &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: coords.left,
              top: coords.top,
              transform: "translateY(-50%)",
              width: 260,
              zIndex: 10000,
            }}
            className="bg-[#404040] text-white text-[12.5px] leading-snug rounded-lg px-3 py-2 shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
          >
            <span className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-[6px] border-transparent border-r-[#404040]" />
            {text}
          </div>,
          document.body
        )}
    </span>
  );
};

const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-[1px]">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);


const AddRequirementModal = ({ isOpen, onClose, onSubmit, submitting }) => {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCategory("");
      setDescription("");
      setCatOpen(false);
    }
  }, [isOpen]);

  // Close the category dropdown on an outside click.
  useEffect(() => {
    if (!catOpen) return;
    const onDown = (e) => {
      if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [catOpen]);

  if (!isOpen) return null;

  const canSubmit = !!category && !!description.trim() && !submitting;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4 font-outfit"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="w-[460px] max-w-full bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.18)] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-[17px] font-semibold text-[#3F3F46]">Add Custom Requirement</h2>
            <p className="text-[13px] text-[#767676] mt-0.5">Define a custom requirement for this proposal</p>
          </div>
          <button
            onClick={() => !submitting && onClose()}
            className="text-gray-400 hover:text-gray-600 transition-colors -mr-1 -mt-1"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Category */}
        <div className="mt-5" ref={catRef}>
          <label className="block text-[13px] font-medium text-[#3F3F46] mb-1.5">
            Requirement Category <span className="text-[#E5484D]">*</span>
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setCatOpen((o) => !o)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 border rounded-md text-[14px] text-left transition ${
                catOpen ? "border-[#8B55DD]" : "border-[#0000001F] hover:border-[#8B55DD]"
              }`}
            >
              <span className={category ? "text-[#3F3F46]" : "text-[#9CA3AF]"}>
                {category || "Select a category"}
              </span>
              <Chevron open={catOpen} />
            </button>

            {catOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-[#0000001A] rounded-md shadow-[0_6px_20px_rgba(0,0,0,0.12)] py-1 max-h-[240px] overflow-auto custom-scrollbar">
                {CATEGORIES.map((c) => {
                  const selected = category === c.label;
                  return (
                    <button
                      key={c.label}
                      type="button"
                      onClick={() => {
                        setCategory(c.label);
                        setCatOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-[14px] flex items-center gap-2 transition-colors ${
                        selected ? "bg-[#F3F4F6] text-[#3F3F46]" : "text-[#3F3F46] hover:bg-[#F7F7F8]"
                      }`}
                    >
                      <span className="truncate">{c.label}</span>
                      {c.hint && <HelpIcon text={c.hint} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="mt-4">
            <label className="block text-[13px] font-medium text-[#3F3F46] mb-1.5">
              Description <span className="text-[#E5484D]">*</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) onSubmit(category, description.trim());
              }}
              placeholder="e.g. Risk Assessment"
              style={{ fontWeight: 400 }}
              className="w-full px-3 py-2.5 border border-[#0000001F] rounded-md text-[14px] text-[#3F3F46] focus:outline-none focus:border-[#8B55DD] transition"
            />
            <p className="mt-2 flex items-start gap-1.5 text-[12px] text-[#767676] leading-snug">
              <InfoIcon />
              <span>
                Enter a name and brief description. You can generate this document next and export
                it along with your proposal.
              </span>
            </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="px-4 py-2 rounded-md border border-[#8B55DD] text-[#8B55DD] text-[14px] font-medium hover:bg-[#F6F1FE] transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => canSubmit && onSubmit(category, description.trim())}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-[14px] font-medium transition-opacity bg-gradient-to-br from-[#6366F1] to-[#8B55DD] disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-95"
          >
            {submitting && (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {submitting ? "Adding..." : "Add Requirement"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRequirementModal;
